<script setup lang="ts">
/**
 * The elements of a free layer: adding them, ordering them, and setting the
 * one that is selected.
 *
 * Placing is done on the page, by hand, because that is what free placement
 * is. What is here is what a hand is bad at: a size to the millimetre, an
 * alignment to the edge, a colour, an order, and adding a new element in the
 * first place. The fields read the element that is selected on the page and
 * the page shows what the fields set, so the two are one control.
 *
 * A new element lands in the middle of the sheet, which is the one place on
 * every page that is certainly in view, and a text lands already open for
 * typing, since a text with nothing in it is not yet anything.
 *
 * With several elements selected, the settings of any one of them would be a
 * guess at which one was meant, so the panel offers only what applies to all:
 * lining them up with one another, spreading them evenly, copying them and
 * taking them away.
 */
import { useI18n } from 'vue-i18n'

import { computed, watch } from 'vue'

import TextFormatTools from '@/components/builder/TextFormatTools.vue'
import ChartFields from '@/components/charts/ChartFields.vue'
import { useFreeLayer } from '@/composables/useFreeLayer'
import { useRememberedTab } from '@/composables/useRememberedTab'
import {
  alignTo,
  centred,
  copyOf,
  coveredBy,
  DEFAULT_SIZES,
  distribute,
  fittedToImage,
  FREE_ALIGNS,
  IMAGE_FITS,
  LEAST_MM,
  newFreeElement,
  normaliseAngle,
  pasted,
  reorder,
  restack,
  SHAPE_FORMS,
  sheetFrame,
  sheetMetricsOf,
  tidyBox,
  type Alignment,
  type FreeAlign,
  type FreeChart,
  type FreeElement,
  type FreeImage,
  type FreeKind,
  type FreeShape,
  type FreeText,
  type ImageFit,
  type ShapeForm,
} from '@/domain/free'
import type { ChartContent } from '@/domain/chart'
import { readAccent } from '@/domain/layout/colour'
import { textOf } from '@/domain/sanitise'
import { newId, type LayerSection } from '@/domain/section'
import { sheetHolding, sheetsOf } from '@/domain/sheets'
import ImagePicker from '@/sections/ImagePicker.vue'
import InspectorField from '@/sections/InspectorField.vue'
import InspectorTabs from '@/sections/InspectorTabs.vue'
import { TAB_ICONS, type InspectorTab } from '@/sections/inspectorTabs'
import TextStyleField from '@/sections/TextStyleField.vue'
import { FREE_COLOURS } from '@/sections/layer'
import AlignButtons from '@/sections/layer/AlignButtons.vue'
import ElementHeader from '@/sections/layer/ElementHeader.vue'
import LayerList from '@/sections/layer/LayerList.vue'
import NothingSelected from '@/sections/layer/NothingSelected.vue'
import { aspectOf } from '@/sections/layer/picture'
import CellColourPicker from '@/sections/table/CellColourPicker.vue'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()

const props = defineProps<{ section: LayerSection }>()
const emit = defineEmits<{ change: [section: LayerSection] }>()

const report = useReportStore()
const free = useFreeLayer()

/** The sheet this layer stands on: a double page is twice as wide, and its middle moves. */
const metrics = computed(() => {
  const sheets = sheetsOf(report.sections, { chapterPageBreak: report.layout.chapterPageBreak })
  const sheet = sheetHolding(sheets, props.section.id)
  return sheetMetricsOf(report.layout, sheet?.spread ?? false)
})

const selected = computed(
  () => props.section.elements.find((element) => element.id === free.selectedId.value) ?? null,
)

/** The elements of this layer selected together, when there are several. */
const many = computed(() => {
  const together = props.section.elements.filter((element) => free.isSelected(element.id))
  return together.length > 1 ? together : []
})

/** Top-most first, as a design tool lists them: what is in front is what is seen. */
const listed = computed(() => [...props.section.elements].reverse())

// ── Tabs ─────────────────────────────────────────────────────────────────
//
// Adding, the list, the element's own settings and where it stands, each in
// a tab of its own, so none is found by scrolling past the others.

type LayerTab = 'add' | 'layers' | 'element' | 'arrange'

const LAYER_TABS: readonly LayerTab[] = ['add', 'layers', 'element', 'arrange']

const tabs = computed<InspectorTab<LayerTab>[]>(() => [
  { id: 'add', label: t('inspector.layer.tabs.add'), icon: TAB_ICONS.add },
  {
    id: 'layers',
    label: t('inspector.layer.tabs.layers'),
    icon: TAB_ICONS.layers,
    count: props.section.elements.length,
  },
  { id: 'element', label: t('inspector.layer.tabs.element'), icon: TAB_ICONS.element },
  { id: 'arrange', label: t('inspector.layer.tabs.arrange'), icon: TAB_ICONS.arrange },
])

/**
 * The tab last used on a layer, unless it has nothing to show: an element
 * picked on the page opens on its settings; with none picked, the layer
 * opens on its list, or on adding one when it is empty.
 */
const tab = useRememberedTab('layer', LAYER_TABS)
const pickedAtOpening = selected.value !== null || many.value.length > 1
if (pickedAtOpening && (tab.value === 'add' || tab.value === 'layers')) tab.value = 'element'
if (!pickedAtOpening && (tab.value === 'element' || tab.value === 'arrange')) {
  tab.value = props.section.elements.length > 0 ? 'layers' : 'add'
}

// An element picked on the page while adding shows its settings; several
// picked while setting one show how to line them up. Picking from the list
// keeps the list open, since that is where the picking is being done.
watch(
  () => [selected.value?.id ?? null, many.value.length] as const,
  ([id, count], [before, counted]) => {
    if (count > 1 && counted <= 1 && tab.value === 'element') tab.value = 'arrange'
    else if (id !== null && id !== before && tab.value === 'add') tab.value = 'element'
  },
)

function commit(elements: readonly FreeElement[]): void {
  emit('change', { ...props.section, elements })
}

function replace(updated: FreeElement): void {
  commit(props.section.elements.map((element) => (element.id === updated.id ? updated : element)))
}

// ── Adding ───────────────────────────────────────────────────────────────

type Adding = 'text' | 'image' | 'chart' | 'rectangle' | 'ellipse' | 'line'

const ADDABLE: readonly Adding[] = ['text', 'image', 'chart', 'rectangle', 'ellipse', 'line']

/** Each kind's pictogram, drawn on a 16-unit square. */
const ADD_ICONS: Record<Adding, readonly string[]> = {
  text: ['M3 3.5h10M8 3.5v9M6 12.5h4'],
  image: [
    'M4 3h8a1.5 1.5 0 0 1 1.5 1.5v7a1.5 1.5 0 0 1-1.5 1.5H4a1.5 1.5 0 0 1-1.5-1.5v-7A1.5 1.5 0 0 1 4 3z',
    'M3 11.5l3.2-3 2.2 2 1.6-1.4 3 2.6',
  ],
  chart: ['M3.5 13V8.5M8 13V3.5M12.5 13V6.5M2 13.5h12'],
  rectangle: ['M3.5 3.5h9a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z'],
  ellipse: ['M2.5 8a5.5 4.5 0 1 0 11 0a5.5 4.5 0 1 0-11 0'],
  line: ['M2.5 12.5l11-9'],
}

function add(what: Adding): void {
  const kind: FreeKind = what === 'text' || what === 'image' || what === 'chart' ? what : 'shape'
  const size =
    what === 'line'
      ? { width: 80, height: 4 }
      : what === 'ellipse'
        ? { width: 50, height: 50 }
        : DEFAULT_SIZES[kind]

  const id = newId()
  let element = newFreeElement(kind, id, centred(metrics.value, size))

  if (element.kind === 'shape' && what === 'ellipse') element = { ...element, form: 'ellipse' }
  if (element.kind === 'shape' && what === 'line') {
    element = { ...element, form: 'line', fill: '', stroke: '#16202c', strokeWidth: 1 }
  }

  commit([...props.section.elements, element])
  free.select(id)
  tab.value = 'element'
  if (kind === 'text') free.edit(id)
}

// ── The list ─────────────────────────────────────────────────────────────

function labelOf(element: FreeElement): string {
  if (element.kind === 'text') {
    const words = textOf(element.html).trim()
    return words ? words.slice(0, 40) : t('inspector.layer.untitledText')
  }
  if (element.kind === 'image') return t('inspector.layer.kinds.image')
  if (element.kind === 'chart') {
    return (
      element.name.trim() || element.chart.labels.join(' · ') || t('inspector.layer.kinds.chart')
    )
  }
  return t(`inspector.layer.kinds.${element.form}`)
}

/** What kind of element it is, as the list names it. */
function kindOf(element: FreeElement): string {
  return t(`inspector.layer.kinds.${element.kind === 'shape' ? element.form : element.kind}`)
}

/** The list's rows, front first: what each element is and what it reads. */
const rows = computed(() =>
  listed.value.map((element) => ({
    id: element.id,
    kind: kindOf(element),
    label: labelOf(element),
  })),
)

function withElement(id: string, act: (element: FreeElement) => void): void {
  const element = props.section.elements.find((candidate) => candidate.id === id)
  if (element) act(element)
}

function pickById(id: string, event: MouseEvent): void {
  withElement(id, (element) => pick(element, event))
}

/** Rows dragged to a gap in the list: the elements stacked again, the list being front first. */
function restackAt(moving: string[], gap: number): void {
  const next = restack(listed.value, new Set(moving), gap)
  if (next.every((element, at) => element === listed.value[at])) return
  commit([...next].reverse())
}

function order(element: FreeElement, direction: -1 | 1): void {
  commit(reorder(props.section.elements, element.id, direction))
}

function duplicate(element: FreeElement): void {
  const copy = copyOf(element, newId())
  const elements = [...props.section.elements]
  elements.splice(elements.indexOf(element) + 1, 0, copy)
  commit(elements)
  free.select(copy.id)
}

function remove(element: FreeElement): void {
  commit(props.section.elements.filter((candidate) => candidate.id !== element.id))
  if (free.selectedId.value === element.id) free.select(null)
}

// ── The selected element ─────────────────────────────────────────────────

function typed(event: Event): number {
  return Number((event.target as HTMLInputElement).value)
}

function setBox(part: 'x' | 'y' | 'width' | 'height', event: Event): void {
  const value = typed(event)
  const element = selected.value
  if (!element || !Number.isFinite(value)) return
  const sized = part === 'width' || part === 'height' ? Math.max(value, LEAST_MM) : value
  replace({ ...element, box: tidyBox({ ...element.box, [part]: sized }) })
}

/** One element lines up with the page. */
function align(how: Alignment): void {
  const element = selected.value
  if (!element) return
  replace({ ...element, box: tidyBox(alignTo(element, sheetFrame(metrics.value), how)) })
}

/** Several line up with the rectangle around them, which is with one another. */
function alignMany(how: Alignment): void {
  const frame = coveredBy(many.value)
  if (!frame) return
  const ids = new Set(many.value.map((element) => element.id))
  commit(
    props.section.elements.map((element) =>
      ids.has(element.id) ? { ...element, box: tidyBox(alignTo(element, frame, how)) } : element,
    ),
  )
}

function spreadMany(axis: 'x' | 'y'): void {
  const moved = distribute(many.value, axis)
  commit(
    props.section.elements.map((element) => {
      const box = moved.get(element.id)
      return box ? { ...element, box: tidyBox(box) } : element
    }),
  )
}

function duplicateMany(): void {
  const copies = pasted(many.value, props.section.elements, newId)
  commit([...props.section.elements, ...copies])
  free.select(copies.map((copy) => copy.id))
}

function removeMany(): void {
  const ids = new Set(many.value.map((element) => element.id))
  commit(props.section.elements.filter((element) => !ids.has(element.id)))
  free.select(null)
}

function setRotation(event: Event): void {
  const element = selected.value
  const value = typed(event)
  if (!element || !Number.isFinite(value)) return
  replace({ ...element, rotation: normaliseAngle(value) })
}

/** A row picks its element, or with Shift adds it to the selection or takes it out. */
function pick(element: FreeElement, event: MouseEvent): void {
  if (event.shiftKey) free.toggle(element.id)
  else free.select(element.id)
}

function setOpacity(event: Event): void {
  const element = selected.value
  if (!element) return
  replace({ ...element, opacity: Math.min(Math.max(typed(event) / 100, 0), 1) })
}

const text = computed(() => (selected.value?.kind === 'text' ? selected.value : null))
const picture = computed(() => (selected.value?.kind === 'image' ? selected.value : null))
const shape = computed(() => (selected.value?.kind === 'shape' ? selected.value : null))
const chart = computed<FreeChart | null>(() =>
  selected.value?.kind === 'chart' ? selected.value : null,
)

function setText(changes: Partial<Omit<FreeText, 'id' | 'kind'>>): void {
  if (text.value) replace({ ...text.value, ...changes })
}

function setPicture(changes: Partial<Omit<FreeImage, 'id' | 'kind'>>): void {
  if (picture.value) replace({ ...picture.value, ...changes })
}

/**
 * A new picture, and a frame shaped to it.
 *
 * The frame keeps its width and takes the picture's proportions, so choosing a
 * portrait photograph for a frame drawn landscape does not leave it cropped to
 * a strip. Measured after it is set, so the picture shows at once and the
 * frame follows as soon as the picture has been read.
 */
async function choosePicture(source: string): Promise<void> {
  const element = picture.value
  if (!element) return
  replace({ ...element, source })
  if (!source) return

  const aspect = await aspectOf(source)
  const current = props.section.elements.find((candidate) => candidate.id === element.id)
  if (current?.kind === 'image' && current.source === source) {
    replace({ ...current, box: fittedToImage(current.box, aspect) })
  }
}

function setShape(changes: Partial<Omit<FreeShape, 'id' | 'kind'>>): void {
  if (shape.value) replace({ ...shape.value, ...changes })
}

function setChart(changes: Partial<ChartContent>): void {
  if (chart.value) replace({ ...chart.value, chart: { ...chart.value.chart, ...changes } })
}

/** A number typed into a field, held between two bounds. */
function bounded(event: Event, min: number, max: number): number | null {
  const value = typed(event)
  return Number.isFinite(value) ? Math.min(Math.max(value, min), max) : null
}

/** The colours offered: the document's accent, then the house's own. */
const colours = computed(() => [
  {
    id: 'accent',
    colour: readAccent(report.layout.accent),
    name: t('inspector.layer.colours.accent'),
  },
  ...FREE_COLOURS.map((entry) => ({
    id: entry.id,
    colour: entry.colour,
    name: t(`inspector.layer.colours.${entry.id}`),
  })),
])

/** What the picker shows as chosen: a colour, or none for an empty setting. */
function chosen(colour: string): string | undefined {
  return colour === '' ? undefined : colour
}
</script>

<template>
  <InspectorTabs v-model:open="tab" :tabs="tabs" :label="t('inspector.layer.tabs.label')">
    <template #add>
      <div class="fields">
        <div class="tiles" role="group" :aria-label="t('inspector.layer.add')">
          <button v-for="what in ADDABLE" :key="what" type="button" class="tile" @click="add(what)">
            <svg class="tile__icon" viewBox="0 0 16 16" aria-hidden="true">
              <path
                v-for="(d, at) in ADD_ICONS[what]"
                :key="at"
                :d="d"
                fill="none"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span class="tile__name">{{ t(`inspector.layer.adding.${what}`) }}</span>
          </button>
        </div>

        <label class="b-switch magnets">
          <input
            type="checkbox"
            :checked="free.magnets.value"
            @change="free.setMagnets(($event.target as HTMLInputElement).checked)"
          />
          <span class="b-switch__track" />
          <span class="magnets__text">
            {{ t('inspector.layer.magnets') }}
            <span class="magnets__hint">{{ t('inspector.layer.magnetsHint') }}</span>
          </span>
        </label>

        <p class="note">{{ t('inspector.layer.note') }}</p>
      </div>
    </template>

    <template #layers>
      <div class="fields">
        <div v-if="listed.length === 0" class="empty">
          <p class="note">{{ t('inspector.layer.none') }}</p>
          <button type="button" class="b-btn b-btn--soft" @click="tab = 'add'">
            {{ t('inspector.layer.addOne') }}
          </button>
        </div>
        <template v-else>
          <LayerList
            :rows="rows"
            :selected="free.selectedIds.value"
            @pick="pickById"
            @forward="(id) => withElement(id, (element) => order(element, 1))"
            @backward="(id) => withElement(id, (element) => order(element, -1))"
            @duplicate="(id) => withElement(id, duplicate)"
            @remove="(id) => withElement(id, remove)"
            @restack="restackAt"
          />
          <p class="note">{{ t('inspector.layer.layersHint') }}</p>
        </template>
      </div>
    </template>

    <template #element>
      <div v-if="many.length > 1" class="fields">
        <div class="together">
          <p class="together__count">
            {{ t('inspector.layer.together', { count: many.length }) }}
          </p>
          <p class="note">{{ t('inspector.layer.manyText') }}</p>
        </div>
        <button type="button" class="b-btn b-btn--soft" @click="tab = 'arrange'">
          {{ t('inspector.layer.toArrange') }}
        </button>
      </div>

      <NothingSelected
        v-else-if="!selected"
        :elements="listed.length > 0"
        @layers="tab = 'layers'"
        @add="tab = 'add'"
      />

      <div v-else class="fields">
        <ElementHeader
          :kind="kindOf(selected)"
          :label="labelOf(selected)"
          @duplicate="duplicate(selected)"
          @remove="remove(selected)"
        />

        <template v-if="text">
          <div class="pair">
            <InspectorField :label="t('inspector.layer.text.size')">
              <label class="measure">
                <input
                  class="b-input"
                  type="number"
                  step="0.5"
                  min="4"
                  max="200"
                  :value="text.size"
                  @change="
                    (event) => {
                      const size = bounded(event, 4, 200)
                      if (size !== null) setText({ size })
                    }
                  "
                />
                <span class="measure__unit">pt</span>
              </label>
            </InspectorField>
            <InspectorField :label="t('inspector.layer.text.padding')">
              <label class="measure">
                <input
                  class="b-input"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  :value="text.padding"
                  @change="
                    (event) => {
                      const padding = bounded(event, 0, 100)
                      if (padding !== null) setText({ padding })
                    }
                  "
                />
                <span class="measure__unit">mm</span>
              </label>
            </InspectorField>
          </div>

          <InspectorField :label="t('inspector.layer.text.align')">
            <div class="segments" role="radiogroup" :aria-label="t('inspector.layer.text.align')">
              <button
                v-for="option in FREE_ALIGNS"
                :key="option"
                type="button"
                role="radio"
                class="segment"
                :class="{ 'segment--on': option === text.align }"
                :aria-checked="option === text.align"
                @click="setText({ align: option as FreeAlign })"
              >
                {{ t(`inspector.layer.text.aligns.${option}`) }}
              </button>
            </div>
          </InspectorField>

          <TextStyleField :value="text.textStyleId" @change="setText({ textStyleId: $event })" />

          <CellColourPicker
            :label="t('inspector.layer.text.colour')"
            :options="colours"
            :chosen="chosen(text.colour)"
            :none-label="t('inspector.layer.noColour')"
            :custom-label="t('inspector.layer.customColour')"
            @choose="setText({ colour: $event ?? '' })"
          />
          <CellColourPicker
            :label="t('inspector.layer.text.fill')"
            :options="colours"
            :chosen="chosen(text.fill)"
            :none-label="t('inspector.layer.noColour')"
            :custom-label="t('inspector.layer.customColour')"
            @choose="setText({ fill: $event ?? '' })"
          />

          <!-- The words inside, as they are being typed: the same tools as a text block's. -->
          <div class="words">
            <p class="words__title">{{ t('inspector.text.tabs.format') }}</p>
            <TextFormatTools />
          </div>
        </template>

        <template v-if="picture">
          <ImagePicker
            :source="picture.source"
            :label="t('inspector.layer.image.picker')"
            shape="wide"
            @change="choosePicture"
          />

          <InspectorField :label="t('inspector.layer.image.fit')">
            <div class="segments" role="radiogroup" :aria-label="t('inspector.layer.image.fit')">
              <button
                v-for="option in IMAGE_FITS"
                :key="option"
                type="button"
                role="radio"
                class="segment"
                :class="{ 'segment--on': option === picture.fit }"
                :aria-checked="option === picture.fit"
                @click="setPicture({ fit: option as ImageFit })"
              >
                {{ t(`inspector.layer.image.fits.${option}`) }}
              </button>
            </div>
          </InspectorField>

          <InspectorField :label="t('inspector.layer.image.radius')">
            <label class="measure">
              <input
                class="b-input"
                type="number"
                step="0.5"
                min="0"
                max="500"
                :value="picture.radius"
                @change="
                  (event) => {
                    const radius = bounded(event, 0, 500)
                    if (radius !== null) setPicture({ radius })
                  }
                "
              />
              <span class="measure__unit">mm</span>
            </label>
          </InspectorField>
        </template>

        <template v-if="shape">
          <InspectorField :label="t('inspector.layer.shape.form')">
            <div class="segments" role="radiogroup" :aria-label="t('inspector.layer.shape.form')">
              <button
                v-for="option in SHAPE_FORMS"
                :key="option"
                type="button"
                role="radio"
                class="segment"
                :class="{ 'segment--on': option === shape.form }"
                :aria-checked="option === shape.form"
                @click="setShape({ form: option as ShapeForm })"
              >
                {{ t(`inspector.layer.kinds.${option}`) }}
              </button>
            </div>
          </InspectorField>

          <CellColourPicker
            v-if="shape.form !== 'line'"
            :label="t('inspector.layer.shape.fill')"
            :options="colours"
            :chosen="chosen(shape.fill)"
            :none-label="t('inspector.layer.noColour')"
            :custom-label="t('inspector.layer.customColour')"
            @choose="setShape({ fill: $event ?? '' })"
          />
          <CellColourPicker
            :label="t('inspector.layer.shape.stroke')"
            :options="colours"
            :chosen="chosen(shape.stroke)"
            :none-label="t('inspector.layer.noColour')"
            :custom-label="t('inspector.layer.customColour')"
            @choose="setShape({ stroke: $event ?? '' })"
          />

          <div class="pair">
            <InspectorField :label="t('inspector.layer.shape.strokeWidth')">
              <label class="measure">
                <input
                  class="b-input"
                  type="number"
                  step="0.25"
                  min="0"
                  max="20"
                  :value="shape.strokeWidth"
                  @change="
                    (event) => {
                      const strokeWidth = bounded(event, 0, 20)
                      if (strokeWidth !== null) setShape({ strokeWidth })
                    }
                  "
                />
                <span class="measure__unit">pt</span>
              </label>
            </InspectorField>
            <InspectorField
              v-if="shape.form === 'rectangle'"
              :label="t('inspector.layer.shape.radius')"
            >
              <label class="measure">
                <input
                  class="b-input"
                  type="number"
                  step="0.5"
                  min="0"
                  max="500"
                  :value="shape.radius"
                  @change="
                    (event) => {
                      const radius = bounded(event, 0, 500)
                      if (radius !== null) setShape({ radius })
                    }
                  "
                />
                <span class="measure__unit">mm</span>
              </label>
            </InspectorField>
          </div>
        </template>

        <template v-if="chart">
          <InspectorField :label="t('inspector.layer.chart.name')">
            <input
              class="b-input"
              :value="chart.name"
              :placeholder="chart.chart.labels.join(' · ')"
              maxlength="120"
              @input="replace({ ...chart, name: ($event.target as HTMLInputElement).value })"
            />
          </InspectorField>

          <ChartFields :chart="chart.chart" @change="setChart" />
        </template>
      </div>
    </template>

    <template #arrange>
      <div v-if="many.length > 1" class="fields">
        <div class="together">
          <p class="together__count">
            {{ t('inspector.layer.together', { count: many.length }) }}
          </p>
          <p class="note">{{ t('inspector.layer.togetherHint') }}</p>
        </div>

        <InspectorField :label="t('inspector.layer.alignTogether')">
          <AlignButtons
            spreading
            :can-spread="many.length > 2"
            @align="alignMany"
            @spread="spreadMany"
          />
        </InspectorField>

        <div class="together__ops">
          <button type="button" class="b-btn b-btn--outline" @click="duplicateMany">
            {{ t('inspector.layer.duplicate') }}
          </button>
          <button type="button" class="b-btn b-btn--outline b-btn--danger" @click="removeMany">
            {{ t('inspector.layer.removeAll', { count: many.length }) }}
          </button>
        </div>
      </div>

      <NothingSelected
        v-else-if="!selected"
        :elements="listed.length > 0"
        @layers="tab = 'layers'"
        @add="tab = 'add'"
      />

      <div v-else class="fields">
        <ElementHeader
          :kind="kindOf(selected)"
          :label="labelOf(selected)"
          @duplicate="duplicate(selected)"
          @remove="remove(selected)"
        />

        <InspectorField :label="t('inspector.layer.position')">
          <div class="quad">
            <label class="measure">
              <span class="measure__name">{{ t('inspector.layer.x') }}</span>
              <input
                class="b-input"
                type="number"
                step="0.5"
                :value="selected.box.x"
                @change="setBox('x', $event)"
              />
              <span class="measure__unit">mm</span>
            </label>
            <label class="measure">
              <span class="measure__name">{{ t('inspector.layer.y') }}</span>
              <input
                class="b-input"
                type="number"
                step="0.5"
                :value="selected.box.y"
                @change="setBox('y', $event)"
              />
              <span class="measure__unit">mm</span>
            </label>
            <label class="measure">
              <span class="measure__name">{{ t('inspector.layer.width') }}</span>
              <input
                class="b-input"
                type="number"
                step="0.5"
                :min="LEAST_MM"
                :value="selected.box.width"
                @change="setBox('width', $event)"
              />
              <span class="measure__unit">mm</span>
            </label>
            <label class="measure">
              <span class="measure__name">{{ t('inspector.layer.height') }}</span>
              <input
                class="b-input"
                type="number"
                step="0.5"
                :min="LEAST_MM"
                :value="selected.box.height"
                @change="setBox('height', $event)"
              />
              <span class="measure__unit">mm</span>
            </label>
          </div>
        </InspectorField>

        <InspectorField :label="t('inspector.layer.alignOnPage')">
          <AlignButtons @align="align" />
        </InspectorField>

        <InspectorField :label="t('inspector.layer.rotation')">
          <div class="turning">
            <label class="measure">
              <input
                class="b-input"
                type="number"
                step="1"
                min="-180"
                max="180"
                :value="selected.rotation"
                @change="setRotation"
              />
              <span class="measure__unit">°</span>
            </label>
            <button
              type="button"
              class="b-btn b-btn--outline"
              :disabled="selected.rotation === 0"
              @click="replace({ ...selected, rotation: 0 })"
            >
              {{ t('inspector.layer.upright') }}
            </button>
          </div>
        </InspectorField>

        <InspectorField :label="t('inspector.layer.opacity')">
          <div class="slider">
            <input
              class="slider__range"
              type="range"
              min="0"
              max="100"
              step="1"
              :value="Math.round(selected.opacity * 100)"
              :aria-label="t('inspector.layer.opacity')"
              @input="setOpacity"
            />
            <span class="slider__value">{{ Math.round(selected.opacity * 100) }} %</span>
          </div>
        </InspectorField>
      </div>
    </template>
  </InspectorTabs>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.note {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.5;
}

/* What can be added, as tiles: a pictogram over its name, three to a row. */
.tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--sp-3);
}

.tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  min-width: 0;
  height: 64px;
  padding: var(--sp-3) var(--sp-2);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius);
  background: var(--panel-raised);
  color: var(--ink-soft);
  font: inherit;
  cursor: pointer;
  transition:
    border-color var(--quick),
    background var(--quick),
    color var(--quick);
}

.tile:hover {
  border-color: var(--accent-line);
  background: var(--accent-quiet);
  color: var(--accent);
}

.tile:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--accent-line);
}

.tile__icon {
  width: 20px;
  height: 20px;
}

.tile__name {
  max-width: 100%;
  overflow: hidden;
  font-size: var(--text-xs);
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.magnets {
  align-items: flex-start;
}

.words {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
  padding-top: var(--sp-5);
  border-top: 1px solid var(--hairline);
}

.words__title {
  margin: 0;
  color: var(--ink);
  font-size: var(--text-sm);
  font-weight: 600;
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sp-4);
  padding: var(--sp-6) var(--sp-5);
  border: 1px dashed var(--hairline-strong);
  border-radius: var(--radius);
}

.magnets__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--text-base);
}

.magnets__hint {
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.5;
}

/* ---------- Fields ---------- */

.turning {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--sp-3);
}

.together {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  padding: var(--sp-4) var(--sp-5);
  border-radius: var(--radius);
  background: var(--accent-quiet);
}

.together__count {
  margin: 0;
  color: var(--accent);
  font-size: var(--text-sm);
  font-weight: 600;
}

.together__ops {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--sp-3);
}

.quad {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--sp-3);
}

.pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--sp-4);
}

.measure {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--sp-2);
}

.measure__name {
  flex: none;
  min-width: 1.4em;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.measure .b-input {
  padding-right: var(--sp-7);
  font-variant-numeric: tabular-nums;
}

.measure__unit {
  position: absolute;
  right: var(--sp-4);
  color: var(--ink-faint);
  font-size: var(--text-xs);
  pointer-events: none;
}

.slider {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
}

.slider__range {
  flex: 1;
  min-width: 0;
  accent-color: var(--accent);
}

.slider__value {
  flex: none;
  min-width: 3.2em;
  color: var(--ink-soft);
  font-size: var(--text-sm);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.segments {
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  padding: 2px;
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline-strong);
}

.segment {
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  padding: 0 var(--sp-3);
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-xs);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.segment:hover {
  color: var(--ink);
}

.segment--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}
</style>
